import { DEFAULT_LOCALE, isLocaleCode, type LocaleCode } from "./locale-config";
import {
  legacyEnglishPathToRouteId,
  localizedPath as manifestLocalizedPath,
  normalizePublicPath,
  resolveLegacyRedirect,
  resolveRouteFromPath,
  routeIdToEnglishCanonicalPath,
  switchLocalePath,
  type RouteId,
} from "./route-manifest";

export type { LocaleCode } from "./locale-config";
export type { RouteId } from "./route-manifest";

export type ParsedLocalizedPath = {
  locale: LocaleCode;
  /** English canonical path used for metadata lookup (e.g. `/services/foo`). */
  canonicalPath: string;
  /** Full localized public path. */
  localizedPath: string;
  routeId: RouteId | null;
};

function resolveRouteId(pathname: string): RouteId | null {
  const resolved = resolveRouteFromPath(pathname);
  if (resolved?.route) return resolved.route.routeId;
  const segments = normalizePublicPath(pathname).split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase() ?? "";
  if (isLocaleCode(first)) {
    const remainder = segments.length <= 1 ? "/" : `/${segments.slice(1).join("/")}`;
    return legacyEnglishPathToRouteId(remainder);
  }
  return legacyEnglishPathToRouteId(pathname);
}

/** Build the localized public path for an English canonical path and target locale. */
export function localizePath(canonicalPath: string, locale: LocaleCode): string {
  const routeId = resolveRouteId(canonicalPath);
  if (routeId) return manifestLocalizedPath(routeId, locale);
  return manifestLocalizedPath("home", locale);
}

/** Returns the URL locale segment (always non-empty). */
export function localeToPathSegment(locale: LocaleCode): string {
  return locale;
}

/** Parses a public pathname into locale, English canonical path, and route ID. */
export function parseLocalizedPath(pathname: string): ParsedLocalizedPath {
  const normalized = normalizePublicPath(pathname);
  const resolved = resolveRouteFromPath(normalized);

  if (resolved?.route) {
    return {
      locale: resolved.locale,
      canonicalPath: routeIdToEnglishCanonicalPath(resolved.route.routeId),
      localizedPath: normalized,
      routeId: resolved.route.routeId,
    };
  }

  const segments = normalized.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase() ?? "";

  if (isLocaleCode(first)) {
    const remainder = segments.length <= 1 ? "/" : `/${segments.slice(1).join("/")}`;
    const routeId = legacyEnglishPathToRouteId(remainder);
    return {
      locale: first,
      canonicalPath: routeId ? routeIdToEnglishCanonicalPath(routeId) : remainder,
      localizedPath: normalized,
      routeId,
    };
  }

  const routeId = legacyEnglishPathToRouteId(normalized);
  return {
    locale: DEFAULT_LOCALE,
    canonicalPath: routeId ? routeIdToEnglishCanonicalPath(routeId) : normalized,
    localizedPath: normalized,
    routeId,
  };
}

export { resolveLegacyRedirect, switchLocalePath, manifestLocalizedPath as localizedRoutePath };
